import { DateTime } from "luxon";
import { classes } from "polytype";
import semverCompare from "semver-compare";
import {
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { config } from "../../config/config.js";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import type { SystemSoftware } from "../SystemSoftware";
import { FeePeriod } from "./FeePeriod";
import type {
  SystemSoftwareVersionData,
  SystemSoftwareVersionListItemData,
  SystemSoftwareVersionListQueryData,
} from "./types.js";

export class SystemSoftwareVersion extends ReferenceModel {
  public readonly systemSoftware: SystemSoftware;

  public constructor(id: string, systemSoftware: SystemSoftware) {
    super(id);
    this.systemSoftware = systemSoftware;
  }

  public static ofId(id: string, systemSoftware: SystemSoftware) {
    return new SystemSoftwareVersion(id, systemSoftware);
  }

  public static find = provideReact(
    async (id: string, systemSoftware: SystemSoftware) => {
      const data = await config.behaviors.systemSoftwareVersion.find(
        id,
        systemSoftware.id,
      );
      if (data) {
        return new SystemSoftwareVersionDetailed(data, systemSoftware);
      }
    },
  );

  public static get = provideReact(
    async (id: string, systemSoftware: SystemSoftware) => {
      const systemSoftwareVersion = await this.find(id, systemSoftware);
      assertObjectFound(systemSoftwareVersion, "systemSoftwareVersion", id);
      return systemSoftwareVersion;
    },
  );

  public findDetailed = provideReact(
    () => SystemSoftwareVersion.find(this.id, this.systemSoftware),
    [this.id],
  ) as AsyncResourceVariant<
    () => Promise<SystemSoftwareVersionDetailed | undefined>
  >;

  public getDetailed = provideReact(
    () => SystemSoftwareVersion.get(this.id, this.systemSoftware),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<SystemSoftwareVersionDetailed>>;

  public static query(
    systemSoftware: SystemSoftware,
    query: SystemSoftwareVersionListQueryData = {},
  ) {
    return new SystemSoftwareVersionListQuery(systemSoftware, query);
  }
}

class SystemSoftwareVersionCommon extends classes(
  DataModel<SystemSoftwareVersionData | SystemSoftwareVersionListItemData>,
  SystemSoftwareVersion,
) {
  public readonly version: string;
  public readonly expiryDate?: DateTime;
  public constructor(
    data: SystemSoftwareVersionData | SystemSoftwareVersionListItemData,
    systemSoftware: SystemSoftware,
  ) {
    super([data], [data.id, systemSoftware]);
    this.version = data.externalVersion;
    this.expiryDate = data.expiryDate
      ? DateTime.fromISO(data.expiryDate)
      : undefined;
  }

  public checkCurrentFee() {
    if (this.data.fee && "periods" in this.data.fee) {
      const period = this.data.fee.periods.find(
        (period) =>
          period.feeValidFrom &&
          DateTime.fromISO(period.feeValidFrom) <= DateTime.now() &&
          (!period.feeValidUntil ||
            DateTime.fromISO(period.feeValidUntil) > DateTime.now()),
      );
      if (period) {
        return new FeePeriod(period);
      }
    }
  }

  public checkImminentFee() {
    if (
      this.data.fee &&
      "periods" in this.data.fee &&
      !this.checkCurrentFee()
    ) {
      const period = this.data.fee.periods.find(
        (period) =>
          period.feeValidFrom &&
          DateTime.fromISO(period.feeValidFrom) > DateTime.now() &&
          DateTime.fromISO(period.feeValidFrom) <
            DateTime.now().plus({ month: 6 }),
      );

      if (period) {
        return new FeePeriod(period);
      }
    }
  }

  public checkImminentExpiryDate() {
    if (
      this.data.expiryDate &&
      DateTime.fromISO(this.data.expiryDate) < DateTime.now().plus({ month: 6 })
    ) {
      return this.data.expiryDate;
    }
  }

  public compare(other: SystemSoftwareVersionListItem): 0 | 1 | -1 {
    return semverCompare(this.data.internalVersion, other.data.internalVersion);
  }
}

export class SystemSoftwareVersionDetailed extends classes(
  SystemSoftwareVersionCommon,
  DataModel<SystemSoftwareVersionData>,
) {
  public constructor(
    data: SystemSoftwareVersionData,
    systemSoftware: SystemSoftware,
  ) {
    super([data, systemSoftware], [data]);
  }
}

export class SystemSoftwareVersionListItem extends classes(
  SystemSoftwareVersionCommon,
  DataModel<SystemSoftwareVersionListItemData>,
) {
  public constructor(
    data: SystemSoftwareVersionListItemData,
    systemSoftware: SystemSoftware,
  ) {
    super([data, systemSoftware], [data]);
  }
}

export class SystemSoftwareVersionListQuery extends ListQueryModel<SystemSoftwareVersionListQueryData> {
  public readonly systemSoftware: SystemSoftware;

  public constructor(
    systemSoftware: SystemSoftware,
    query: SystemSoftwareVersionListQueryData,
  ) {
    super(query, { dependencies: [systemSoftware.id] });
    this.systemSoftware = systemSoftware;
  }

  public refine(query: SystemSoftwareVersionListQueryData) {
    return new SystemSoftwareVersionListQuery(this.systemSoftware, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.systemSoftwareVersion.list(
      this.systemSoftware.id,
      this.query,
    );

    return new SystemSoftwareVersionList(
      this.systemSoftware,
      this.query,
      items
        .map((d) => new SystemSoftwareVersionListItem(d, this.systemSoftware))
        .sort((a, b) => b.compare(a)),
    );
  }, [this.queryId, "systemSoftwareVersion"]);
}

export class SystemSoftwareVersionList extends classes(
  SystemSoftwareVersionListQuery,
  ListDataModel<SystemSoftwareVersionListItem>,
) {
  public constructor(
    systemSoftware: SystemSoftware,
    query: SystemSoftwareVersionListQueryData,
    systemSoftwareVersions: SystemSoftwareVersionListItem[],
  ) {
    super(
      [systemSoftware, query],
      [systemSoftwareVersions, systemSoftwareVersions.length],
    );
  }
}
